import React, { useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

interface UnpublishDialogProps {
  event: { id: string; title?: string | null };
  onClose: () => void;
  /** Called after a successful unpublish, so the caller can refresh and re-render. */
  onUnpublished: () => void | Promise<void>;
}

/**
 * Unpublish confirmation (#134, formerly #144 R6). Names the competition and
 * the consequence. On confirm, calls the competition action API's
 * `actionType: 'UNPUBLISH'`. The server writes the COMPETITION_UNPUBLISHED
 * audit entry; this dialog does not write a second one.
 */
const UnpublishDialog: React.FC<UnpublishDialogProps> = ({ event, onClose, onUnpublished }) => {
  const [working, setWorking] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const container = dialogRef.current;
      if (!container) return;
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [onClose]);

  const title = event.title || 'this competition';

  const handleConfirm = async () => {
    setWorking(true);
    setServerError(null);
    try {
      const result = await (client.mutations as any).adjudicateCatch({
        actionType: 'UNPUBLISH',
        eventId: event.id,
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      const data = parseLambdaResponse<{ success?: boolean; error?: string }>(result?.data);
      if (!data?.success) {
        setServerError(data?.error || 'Failed to unpublish results');
        setWorking(false);
        return;
      }
      await onUnpublished();
      onClose();
    } catch (err: any) {
      setServerError(err.message || 'Failed to unpublish results');
      setWorking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-2 sm:p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="unpublish-title"
          className="relative w-full max-w-md rounded-lg bg-white p-4 shadow-xl sm:p-6"
        >
          <div className="flex items-start justify-between gap-3">
            <h2 id="unpublish-title" className="text-lg font-bold text-gray-900">
              Unpublish results
            </h2>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="min-h-[44px] min-w-[44px] shrink-0 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <p className="mt-3 text-sm text-gray-700">
            Unpublish <strong>{title}</strong>? Results disappear from the public site. The mystery fish is hidden again.
          </p>

          {serverError && (
            <p role="alert" aria-live="assertive" className="mt-4 text-sm font-semibold text-red-700">
              {serverError}
            </p>
          )}

          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[44px] rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={working}
              className="min-h-[44px] rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:bg-gray-300"
            >
              {working ? 'Unpublishing…' : `Unpublish ${title}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnpublishDialog;
